import { Link } from "react-router-dom";
import { Section } from "@/components/Section";
import { ArrowRight, Compass, GraduationCap, Wrench, Sparkles } from "lucide-react";

const ADVISE = {
  icon: Compass,
  accent: "#C6A85A",
  eyebrow: "01 · Advise",
  title: "Leadership Strategy and Executive Advisory",
  tagline: "Help us clarify priorities and move forward with confidence.",
  intro:
    "We help leaders clarify priorities, navigate complexity, strengthen decisions, and prepare their organizations to move forward with confidence. When useful, AI readiness and responsible adoption are integrated to support the leadership strategy.",
  services: [
    { name: "Executive Advisory & Leadership Coaching",
      body: "Confidential, one-on-one and executive-team advisory around strategy, operations, communication, and organizational decision-making." },
    { name: "Priority Clarification & Decision Support",
      body: "Structured advisory to help leaders sort the urgent from the important, sharpen decisions, and align the team behind a clear path forward." },
    { name: "Organizational Strategy & Readiness",
      body: "Leadership-grade planning for the next chapter of the organization, with people, systems, and culture at the center." },
    { name: "Workflow & Operational Analysis",
      body: "Diagnostic review of the processes that consume the most leadership capacity, and where a smarter system or practical AI can help." },
    { name: "Responsible AI Readiness & Adoption",
      body: "When useful, we integrate AI readiness (privacy, confidentiality, verification, bias awareness, and human oversight built in) in service of the leadership strategy." },
    { name: "Executive Research & Analysis",
      body: "Decision-ready synthesis and briefs on questions your team doesn't have time to work through alone." },
  ],
};

const EQUIP = {
  icon: GraduationCap,
  accent: "#E33278",
  eyebrow: "02 · Equip",
  title: "Leadership Training, Speaking, and Facilitation",
  tagline: "Help our leaders and teams apply what matters most.",
  intro:
    "We equip leaders and teams through executive learning experiences, strategy labs, institutes, keynotes, retreats, cohorts, and facilitated sessions designed for immediate application. Every experience is anchored to authentic leadership challenges, so participants leave equipped to lead forward tomorrow.",
  services: [
    { name: "LAYOR Executive AI Leadership Institute™",
      body: "A premium, customized, multi-session institutional program for executive teams, associations, and organizations building durable AI leadership capacity. Every Institute is diagnosed, designed, and delivered around the specific enterprise outcome the organization is pursuing. Pricing is customized based on the scope and needs of each engagement, and our team will follow up to discuss your priorities and the appropriate options.",
      cta: { label: "Inquire about the Institute", to: "/quote?service=LAYOR%20Executive%20AI%20Leadership%20Institute" } },
    { name: "Executive Strategy Lab™: Leadership Velocity",
      flagship: true,
      body: "Flagship program. A 90-minute virtual experience, Driving Enterprise Outcomes with Practical AI™. Executives move through three tiers: Tier 1, Diagnose Before You Design (root-cause analysis); Tier 2, Augmented Decision-Making (AI widens analysis only after the leader diagnoses the issue); Tier 3, Disciplined Execution using APPLY → TIMELINE → SUCCESS CHECK → REFINE → EVALUATE. Participants use their preferred AI platform, such as ChatGPT, and leave equipped to lead forward.",
      cta: { label: "Host a Strategy Lab session", to: "/quote?service=Executive%20Strategy%20Lab%20Leadership%20Velocity" } },
    { name: "LAYOR Leadership Lab™",
      body: "A 120-minute hands-on team experience. Leaders build practical tools around work they already lead every day (Leadership Communication Builder, AI Leadership Thought Partner, Personal AI Assistant Blueprint, LAYOR Prompt Blueprint) and leave with a 24-hour implementation commitment so tomorrow's leadership begins right away.",
      cta: { label: "Bring the Leadership Lab to your team", to: "/quote?service=LAYOR%20Leadership%20Lab" } },
    { name: "Executive Keynotes & Conference Sessions",
      body: "Keynote presentations and breakout sessions for executive conferences, associations, and organizational events, grounded in real leadership responsibilities." },
    { name: "Facilitated Executive-Team Retreats",
      body: "Full-day and multi-day retreats where cabinets, boards, and leadership teams work through strategy, AI adoption, and organizational priorities together." },
    { name: "Multi-Session Leadership Cohorts",
      body: "Sequenced cohort programs for organizations that want progressive skill-building instead of one-time sessions." },
  ],
};

const BUILD = {
  icon: Wrench,
  accent: "#0F1D3D",
  eyebrow: "03 · Build",
  title: "Leadership Tools, Decision Support, and Digital Solutions",
  tagline: "Help us build practical systems we can actually use.",
  intro:
    "We create practical systems, custom assistants, decision-support resources, learning tools, and digital solutions that extend leadership capacity and make complex work easier to manage and execute.",
  services: [
    { name: "Decision-Support Systems",
      body: "Structured tools that help leaders think through complex decisions: options, trade-offs, stakeholder impact, and next steps." },
    { name: "Custom Leadership Assistants & Agents",
      body: "Role-specific assistants for executives, cabinets, and teams. Communication support, planning help, research, meeting prep, and follow-through, with human judgment at the center." },
    { name: "Communication & Meeting Systems",
      body: "Prep, agendas, difficult-message builders, follow-up templates, and communication libraries that stay with the organization." },
    { name: "Strategic Planning Tools",
      body: "Customized planning frameworks, prompt libraries, and template systems for cabinets and leadership teams." },
    { name: "Multi-Step Workflows & Automations",
      body: "Coordinated systems that handle multi-stage work (research → draft → review → publish → follow-up) with humans in the loop at the right checkpoints." },
    { name: "Organizational Toolkits & Playbooks",
      body: "Durable, shareable resources so institutional knowledge lives in systems, not just in people's heads." },
  ],
};

const STUDIO_SERVICES = [
  { name: "Executive Websites & Program Sites",
    body: "Professional, responsive, accessible websites for leadership programs, associations, executive brands, and organizational initiatives." },
  { name: "Board & Conference Presentation Design",
    body: "Polished decks for board meetings, executive presentations, keynotes, and organizational events." },
  { name: "Pitch Deck Development",
    body: "Investor, funding, sales, partnership, and sponsorship decks that make the ask crystal clear." },
  { name: "Digital Resources & Toolkits",
    body: "Branded playbooks, guides, prompt libraries, planning workbooks, and organizational reference materials." },
];

function ServiceItem({ item, accent }) {
  return (
    <div
      className={`rounded-2xl border p-6 md:p-7 relative ${item.flagship ? "bg-[var(--layor-navy)] text-white" : "bg-white border-[var(--layor-border)]"}`}
      style={item.flagship ? { borderColor: "rgba(198,168,90,0.5)" } : undefined}
      data-testid={`service-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {item.flagship && (
        <div className="absolute -top-3 left-6 rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.32em] font-semibold" style={{ backgroundColor: "#E6C871", color: "#0A152C" }}>
          Flagship
        </div>
      )}
      <h3 className={`font-display text-lg md:text-xl leading-snug ${item.flagship ? "text-white" : "text-[var(--layor-navy)]"}`}>
        {item.name}
      </h3>
      <p className={`mt-3 text-sm leading-relaxed ${item.flagship ? "text-white/85" : "text-[var(--layor-muted)]"}`}>
        {item.body}
      </p>
      {item.cta && (
        <Link
          to={item.cta.to}
          className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${item.flagship ? "text-[var(--layor-gold)] hover:text-white" : "text-[var(--layor-navy)] hover:text-[var(--layor-pink)]"} transition-colors`}
        >
          {item.cta.label} <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

function Pillar({ pillar }) {
  const Icon = pillar.icon;
  return (
    <div id={pillar.eyebrow.split(" · ")[1].toLowerCase()} className="scroll-mt-24">
      <div className="grid md:grid-cols-12 gap-8 items-start mb-8">
        <div className="md:col-span-8">
          <div className="flex items-center gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: pillar.accent, color: "#fff" }}>
              <Icon size={22} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.35em] font-semibold" style={{ color: pillar.accent }}>
              {pillar.eyebrow}
            </div>
          </div>
          <h2 className="mt-5 font-display text-3xl md:text-4xl leading-[1.1] text-[var(--layor-navy)]">
            {pillar.title}
          </h2>
          <p className="mt-3 italic text-[var(--layor-pink)] font-medium">"{pillar.tagline}"</p>
          <p className="mt-5 text-[var(--layor-muted)] leading-relaxed max-w-2xl">
            {pillar.intro}
          </p>
        </div>
        <div className="md:col-span-4 md:text-right flex md:justify-end gap-3">
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Book a Consultation <ArrowRight size={14} />
          </Link>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 md:gap-5">
        {pillar.services.map((item) => (
          <ServiceItem key={item.name} item={item} accent={pillar.accent} />
        ))}
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* Hero */}
      <Section
        eyebrow="Work With LAYOR"
        title="Three ways to extend leadership capacity, plus a studio for the materials that carry your message."
        kicker="LAYOR Digital Group is an Executive Leadership & Strategic Advisory and transformation firm. Engagements are structured around Advise, Equip, and Build: separately or as an integrated program."
      />

      {/* Pillars */}
      <section className="bg-white border-y border-[var(--layor-border)]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24 space-y-20 md:space-y-28">
          <Pillar pillar={ADVISE} />
          <Pillar pillar={EQUIP} />
          <Pillar pillar={BUILD} />
        </div>
      </section>

      {/* Digital Studio: dedicated design and production division */}
      <Section
        id="studio"
        eyebrow="LAYOR Digital Studio"
        title="The dedicated design and production division of LAYOR Digital Group."
        kicker="LAYOR Digital Studio designs and produces executive websites, board and pitch decks, presentations, and digital resources. We also refine, update, enhance, or redesign existing assets to ensure they are strategically aligned, professionally presented, and ready to support the organization's goals."
      >
        <div className="grid md:grid-cols-2 gap-4 md:gap-5">
          {STUDIO_SERVICES.map((item) => (
            <ServiceItem key={item.name} item={item} accent="#0A152C" />
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/quote"
            className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
          >
            Request a Studio Quote <ArrowRight size={14} />
          </Link>
        </div>
      </Section>

      {/* Final CTA */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0A152C" }}>
        <div className="mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-24 text-center relative">
          <div className="text-[10px] uppercase tracking-[0.35em] font-semibold" style={{ color: "#E6C871" }}>
            Not sure which pillar fits?
          </div>
          <h2 className="mt-6 font-display text-3xl md:text-4xl leading-[1.1] text-white">
            Start with a consultation. We'll tell you honestly whether advisory, training, a built solution, or a mix is right.
          </h2>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
              Book a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/quote" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold border border-white/25 text-white hover:bg-white hover:text-[var(--layor-navy)] transition-colors">
              Request a Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
